import { useAtomValue } from 'jotai'
import { RefObject, useEffect } from 'react'

import { BlockStateEnum } from 'packages/feature-utils/test-paper'
import { PaperBlock } from 'packages/sdks-next/growp'

import { focusedBlockAtom } from './atoms'

const MOUSEDOWN = 'mousedown'
const TOUCHSTART = 'touchstart'
type HandledEvents = [typeof MOUSEDOWN, typeof TOUCHSTART]
type HandledEventsType = HandledEvents[number]
type PossibleEvent = {
  [Type in HandledEventsType]: HTMLElementEventMap[Type]
}[HandledEventsType]
type Handler = (event: PossibleEvent) => void

/**
 * 获取当前选中的块
 *
 * @param blocks - 块列表
 * @returns 当前选中的块
 */
export const useSelectedBlock = (blocks: PaperBlock[]) => {
  const focusedBlock = useAtomValue(focusedBlockAtom)
  return blocks.find(
    (block) =>
      block.id === focusedBlock.id &&
      focusedBlock.state === BlockStateEnum.Selected
  )
}

/**
 * 点击 ref 外触发 handler 事件
 *
 * @param ref - HTMLElement 的 RefObject
 * @param handler - 触发的事件
 * @param boundry - 事件绑定的边界，即在 ref 外，boundry 内触发 handler，默认为 documentElement
 */
export const useClickOutside = (
  ref: RefObject<HTMLElement>,
  handler: Handler | null,
  boundry: HTMLElement | null
) => {
  const boundryElement = boundry || document.documentElement

  useEffect(
    () => {
      const listener = (event: PossibleEvent) => {
        // Do nothing if clicking ref's element or descendent elements
        if (!ref.current || ref.current.contains(event.target as Node)) {
          return
        }

        handler?.(event)
      }

      boundryElement.addEventListener('mousedown', listener)
      boundryElement.addEventListener('touchstart', listener)
      return () => {
        boundryElement.removeEventListener('mousedown', listener)
        boundryElement.removeEventListener('touchstart', listener)
      }
    },
    /*
     * Add ref and handler to effect dependencies
     * It's worth noting that because passed in handler is a new ...
     * ... function on every render that will cause this effect ...
     * ... callback/cleanup to run every render. It's not a big deal ...
     * ... but to optimize you can wrap handler in useCallback before ...
     * ... passing it into this hook.
     */
    [ref, handler, boundryElement]
  )
}
